import { motion as m } from "framer-motion";
import React from "react";
import { Link } from "react-router-dom";
import myCV from "../../CV/my CV.pdf";
import { useCursorContext } from "../../context/CursorContext";
import { useLanguage } from "../../context/LanguageContext";

const AboutPage: React.FC = () => {
  const { mouseOverEvent, mouseOutEvent } = useCursorContext();
  const { t } = useLanguage();

  return (
    <div className="relative w-full h-auto flex justify-start items-start md:items-center pt-28 md:pt-0 px-8 md:px-20">
      {/* Main Content Wrapper */}
      <div className="w-full xl1100:w-[85%] 2xl:w-[75%] h-auto flex flex-col justify-start md:justify-start items-start md:ml-20 xl1100:ml-40 pb-20 md:pt-20">

        {/* Main Content Area - Editorial Split Layout */}
        <div className="z-40 relative w-full flex flex-col xl:flex-row gap-10 xl:gap-16 items-stretch mt-8 md:mt-12 pr-4 md:pr-10">

          {/* Left Column - Image & Skills */}
          <div className="contents xl:flex xl:w-[45%] xl:flex-col xl:justify-start xl:gap-16 xl:relative xl:-mt-16">
            <m.img
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              src="/fotoX.png"
              alt="Esteban Vallejos"
              className="order-3 xl:order-none w-full h-auto object-cover grayscale"
            />

            {/* Skills Block moved to Left Column */}
            <m.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="order-6 xl:order-none pr-8"
            >
              <h3 className="Avegas-Royale-Regular text-xl mb-4 opacity-50 uppercase">{t("about.skills.title")}</h3>
              <div className="flex flex-col gap-6 text-xs sm500:text-sm leading-relaxed opacity-80">
                <div>
                  <span className="font-semibold block mb-1">{t("about.skills.frontend")}</span>
                  React, Astro, Vue, TypeScript, JavaScript, HTML, CSS, Tailwind, Sass, Tauri, Electron
                </div>
                <div>
                  <span className="font-semibold block mb-1">{t("about.skills.backend")}</span>
                  Firebase, Supabase, Rust, C#/.NET, Python, MySQL, MongoDB, SQL Server
                </div>
                <div>
                  <span className="font-semibold block mb-1">{t("about.skills.design")}</span>
                  Figma, Adobe Photoshop, UX/UI Design
                </div>
                <div>
                  <span className="font-semibold block mb-1">{t("about.skills.optimization")}</span>
                  SEO, Agile, Git Best Practices
                </div>
              </div>
            </m.div>
          </div>

          {/* Right Column - Text Content */}
          <div className="contents xl:flex xl:w-[55%] xl:flex-col xl:justify-start xl:pb-20">

            {/* Title */}
            <div className="order-1 xl:order-none overflow-hidden w-full mb-6">
              <m.div
                initial={{ transform: "translateY(100%)" }}
                animate={{ transform: "translateY(0%)" }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="Avegas-Royale-Regular text-6xl sm500:text-7xl md:text-8xl xl:text-[7rem] whitespace-nowrap leading-none"
              >
                {t("about.title")}
              </m.div>
            </div>

            {/* Introduction Text */}
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="order-2 xl:order-none text-2xl md:text-3xl leading-snug mb-12 md:pr-10"
            >
              {t("about.intro")}
            </m.div>

            {/* Grid for Background, Passion & Skills */}
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="order-4 xl:order-none grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 w-full"
            >
              {/* Left Sub-column */}
              <div className="flex flex-col gap-10">
                <div>
                  <h3 className="Avegas-Royale-Regular text-xl mb-4 opacity-50">Background</h3>
                  <p className="text-sm leading-relaxed opacity-80">{t("about.background")}</p>
                </div>

                {/* Experience moved here from the right */}
                <div>
                  <h3 className="Avegas-Royale-Regular text-xl mb-4 opacity-50 uppercase">{t("about.experience.title")}</h3>
                  <div className="flex flex-col gap-6 text-sm">
                    <div>
                      <div className="font-semibold text-base">Sky Hosting Cloud</div>
                      <div className="opacity-80 italic">FullStack Developer</div>
                      <div className="opacity-60 text-xs">Feb 2025 - Feb 2026</div>
                    </div>
                    <div>
                      <div className="font-semibold text-base">Inovatry Solutions</div>
                      <div className="opacity-80 italic">Software Developer</div>
                      <div className="opacity-60 text-xs">Feb 2026 - Present</div>
                    </div>
                    <div>
                      <div className="font-semibold text-base">{t("about.experience.freelance")}</div>
                      <div className="opacity-60 text-xs">Dec 2022 - Present</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Sub-column */}
              <div className="flex flex-col gap-10">
                <div>
                  <h3 className="Avegas-Royale-Regular text-xl mb-4 opacity-50">Passion</h3>
                  {/* Text enlarged as requested */}
                  <p className="text-base md:text-lg lg:text-xl leading-relaxed opacity-80">{t("about.passion")}</p>
                </div>
              </div>
            </m.div>

            {/* Education (Full width below the grid) */}
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              className="order-5 xl:order-none w-full mt-12"
            >
              <h3 className="Avegas-Royale-Regular text-xl mb-6 opacity-50 uppercase">{t("about.education.title")}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 text-sm">
                <div>
                  <div className="font-semibold mb-1">Ingeniería en Sistemas</div>
                  <div className="opacity-75 text-xs mb-1">UISIL</div>
                  <div className="opacity-50 text-xs">2025 - Present</div>
                </div>
                <div>
                  <div className="font-semibold mb-1">Tecnico en Informática</div>
                  <div className="opacity-75 text-xs mb-1">CTP Agroportica</div>
                  <div className="opacity-50 text-xs">2022 - 2024</div>
                </div>
                <div>
                  <div className="font-semibold mb-1">Microsoft Certified</div>
                  <div className="opacity-75 text-xs mb-1">C# Developer</div>
                  <div className="opacity-50 text-xs">2022</div>
                </div>
                <div>
                  <div className="font-semibold mb-1">Google Certified</div>
                  <div className="opacity-75 text-xs mb-1">Full Stack Developer</div>
                  <div className="opacity-50 text-xs">2022</div>
                </div>
              </div>
            </m.div>

            {/* Action Buttons */}
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              className="order-7 xl:order-none flex flex-wrap items-center justify-between gap-6 w-full mt-16 pt-8 border-t border-current border-opacity-10"
            >
              <a
                onMouseOver={mouseOverEvent}
                onMouseOut={mouseOutEvent}
                href={myCV}
                target="_blank"
                rel="noreferrer"
                className="w-auto py-3 px-12 theme-text Avegas-Royale-Regular text-lg md:text-xl text-center hover:opacity-50 duration-300 ease-in-out inline-block border border-current rounded-full"
              >
                {t("about.myCV")}
              </a>

              <Link
                to="/contact"
                onMouseOver={mouseOverEvent}
                onMouseOut={mouseOutEvent}
                className="w-auto py-3 px-12 theme-bg-inv theme-text-inv Avegas-Royale-Regular text-lg md:text-xl text-center rounded-full hover:opacity-80 duration-300 ease-in-out inline-block"
              >
                {t("about.contactMe")}
              </Link>
            </m.div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
